import * as React from "react";
import {
    DefaultButton,
    Pivot,
    PivotItem,
    Stack,
    Separator,
    Spinner,
    ActionButton,
} from "@fluentui/react";
import useAxios from "axios-hooks";
import BasicInfoStep from "./BasicInfoStep";
import DescriptionStep from "./DescriptionStep";
import IconStep from "./IconStep";
import PublishStatusStep from "./PublishStatusStep";
import CategoryStep from "./CategoryStep";
import CompletionStep from "./CompletionStep";
import AudienceStep from "./AudienceStep";
import LearningObjectiveStep from "./LearningObjectiveStep";
import { useCallback, useState, useEffect } from "react";
import { Prompt, useHistory, useParams,useRouteMatch } from "react-router-dom";
import WizardModeIndicator from '../../../../Util/WizardModeIndicator';
import { Formik } from "formik";
import PrimaryButton from "../../../../Forms/PrimaryButton";
import StepsStep from "./StepsStep";
import ActionPlanAnalysisStep from "./ActionPlanAnalysisStep";
import ActionPlanTipStep from "./ActionPlanTipStep";
import Lessons from "./Lessons";
import FeatureSummary from "../../../../Util/FeatureSummary";

const coursesSummary = [{
    selected: "0",
    label: "Basic Info",
    summary:
        "This is the title and slug of the course. You can reach it at 'courses/<slug>'",
    imageUrlPath: "course-specific",
},
{
    selected: "1",
    label: "Description",
    summary:
        "Set the course description that appears on the course overview page. This helps users understand what the course covers.",
    imageUrlPath: "course-specific",
},
{
    selected: "2",
    label: "Icon",
    summary:
        "This is the icon of the course. It appears on the course overview page and course card throughout the site.",
    imageUrlPath: "courses-all2",
},
{
    selected: "3",
    label: "Category",
    summary:
        "Assign the course to a category and tag. These help organize courses and make them easier for users to find.",
},
{
    selected: "4",
    label: "Completion",
    summary:
        "Set up the completion certificate type and details for the course. If enabled, users will be able to access their certificate after completing the course.",
    imageUrlPath: "course-completion",
},
{
    selected: "5",
    label: "Audience",
    summary:
        "Define the target audience for this course. This determines which user groups can access the course content.",
},
{
    selected: "6",
    label: "Learning Objectives",
    summary:
        "Set the learning objectives for the course. These outline what users will learn and accomplish by completing the course.",
},
{
    selected: "7",
    label: "Setup Steps",
    summary:
        "These are the setup steps for the course. Setup steps gather preliminary information and set course subject knowledge baselines.",
    imageUrlPath: "setup-steps",
},
{
    selected: "8",
    label: "Course Steps",
    summary:
        "These are the main steps of the course. They represent the core content and activities that users will engage with.",
    imageUrlPath: "course-step",
},
{
    selected: "9",
    label: "Action Plan Analysis",
    summary:
        "Sets the 'Your Analysis' section on the Action Plan's score page.",
    imageUrlPath: "course-action-plan",
},
{
    selected: "10",
    label: "Action Plan Tip",
    summary:
        "Set the tips that appear on the Action Plan's analysis page.",
    imageUrlPath: "action-plan-tip",
},
{
    selected: "11",
    label: "Publish Status",
    summary:
        "Control the publish status of the course. This determines whether the course is visible and accessible to users.",
}]

export const CreateCourseWizard = () => {
    const history = useHistory();
    let { url } = useRouteMatch();
    let { courseId: courseIdFromParams, revId: revIdFromParams } = useParams();

    const [{ data }, submitForm] = useAxios(
        { url: `courses/` },
        { manual: true }
    );

    const [{ dataTitle }, submitTitle] = useAxios(
        { url: `courses/title` },
        { manual: true }
    );

    const [
        { data: courseData, loading: courseLoading },
        getCourseData,
    ] = useAxios(
        { url: `courses/${courseIdFromParams}`, method: "get" },
        { manual: true }
    );

    const courseId = (data && data.crsid) || courseIdFromParams;

    const [selectedKey, setSelectedKey] = React.useState(0);

    const nextOnClick = useCallback(() => {
        setSelectedKey(Number(selectedKey) + 1);
    }, [selectedKey]);

    const prevOnClick = () => {
        setSelectedKey(Number(selectedKey) - 1);
    };

    const handleLinkClick = (item) => {
        const itemKey = item.props.itemKey;
        setSelectedKey(itemKey);
    };

    useEffect(() => {
        if (courseIdFromParams && !courseData && !courseLoading) {
            getCourseData();
        }
    }, [
        courseData,
        courseIdFromParams,
        courseLoading,
        getCourseData,
    ]);

    if (courseLoading) {
        return <Spinner className="absolute-center" text="Loading Course..." />;
    }

    return (
        <Formik
            initialValues={mapInitialValues(courseData)}
            onSubmit={async (values, { resetForm }) => {
                if (courseId) {
                    var result = await submitTitle({
                        data: {
                            CRSID: courseId,
                            Title:values.title
                        },
                        method: "post",
                        url: `courses/${courseId}/title`,
                    });
                }else{
                    var result = await submitForm({
                        data: mapValuesForSubmit(values),
                        method: "post",
                        url: 'courses',
                    });
                }
                if (!result.isAxiosError && !courseId) {
                    resetForm();
                    history.push(`/course-feature/courses/${result.data.crsid}/${result.data.revNum}/wizard`)
                    nextOnClick();
                }else{
                    nextOnClick();
                }
            }}
            isInitialValid={false}
        >
            {({ dirty }) => {
                const summaryObj = coursesSummary.find(s => String(s.selected) === String(selectedKey));
                return (
                    <>
                        <Prompt
                            when={(dirty) && selectedKey != 10}
                            message="You have unsaved changes, are you sure you want to leave?"
                        />
                        <WizardModeIndicator paramId={courseId} title="Course" />
                        <Pivot
                            selectedKey={String(selectedKey)}
                            onLinkClick={handleLinkClick}
                        >
                                <PivotItem headerText="Basic Info" itemKey="0">
                                    <BasicInfoStep
                                        courseId={courseId}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>
                                {courseId && (<PivotItem headerText="Description" itemKey="1">
                                    <DescriptionStep
                                        courseId={courseId}
                                        revId={revIdFromParams}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>)}
                                {courseId && (<PivotItem headerText="Icon" itemKey="2">
                                    <IconStep
                                        courseId={courseId}
                                        slug={courseData && courseData.slug}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>)}
                                {courseId && ( <PivotItem headerText="Category" itemKey="3">
                                    <CategoryStep
                                        courseId={courseId}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>)}
                                {courseId && (<PivotItem headerText="Completion" itemKey="4">
                                    <CompletionStep
                                        courseId={courseId}
                                        revId={revIdFromParams}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>)}
                                {courseId && (<PivotItem headerText="Audience" itemKey="5">
                                    <AudienceStep
                                        courseId={courseId}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>)}
                                {courseId && (<PivotItem headerText="Learning Objectives" itemKey="6">
                                    <LearningObjectiveStep
                                        courseId={courseId}
                                        revId={revIdFromParams}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>)}
                                {courseId && (<PivotItem headerText="Setup Steps" itemKey="7">
                                    <StepsStep
                                        courseId={courseId}
                                        setup={true}
                                        revId={revIdFromParams}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>)}
                                {courseId && (<PivotItem headerText="Course Steps" itemKey="8">
                                    <StepsStep
                                        courseId={courseId}
                                        setup={false}
                                        revId={revIdFromParams}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>)}
                                {courseId && (<PivotItem headerText="Action Plan Analysis" itemKey="9">
                                    <ActionPlanAnalysisStep
                                        courseId={courseId}
                                        revId={revIdFromParams}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>)}
                                {courseId && (<PivotItem headerText="Action Plan Tip" itemKey="10">
                                    <ActionPlanTipStep
                                        courseId={courseId}
                                        revId={revIdFromParams}
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                    />
                                </PivotItem>)}
                                {courseId && (<PivotItem headerText="PublishStatus" itemKey="11">
                                    <PublishStatusStep
                                        courseId={courseId}
                                        prevOnClick={prevOnClick}
                                    />
                                </PivotItem>)}
                        </Pivot>
                        {summaryObj && (
                            <FeatureSummary
                                label={summaryObj.label}
                                summary={summaryObj.summary}
                                imageUrlPath={summaryObj.imageUrlPath}
                            />
                        )}
                    </>
                );
            }}
        </Formik>
    );
};

export function mapInitialValues(courseData) {
    if (courseData)
        return {
            title: courseData.title,
            slug: courseData.slug
        };
    else return {};
}

function mapValuesForSubmit(values) {
    return { ...values, isActive: true };
}

export const NavigationalButtons = ({
    onDeleteHandler,
    onDeleteProps,
    onNextHandler,
    onNextProps,
    onPrevHandler,
    onPrevProps,
    width,
    loading,
}) => {
    const showPrevious = onPrevHandler || onPrevProps;
    return (
        <>
            <Separator />
            <Stack
                horizontal
                styles={{
                    root: {
                        height: 50,
                        ...(width && { width }),
                    },
                }}
                horizontalAlign={showPrevious ? "space-between" : "end"}
                verticalAlign="center"
            >
                {showPrevious && (
                    <DefaultButton disabled={loading} onClick={onPrevHandler} {...onPrevProps}>
                        {(onPrevProps && onPrevProps.text) || "Prev"}
                    </DefaultButton>
                )}
                <Stack horizontal verticalAlign="center">
                    {onDeleteHandler &&
                        <ActionButton
                            disabled={loading}
                            iconProps={{ iconName: 'Delete' }}
                            onClick={onDeleteHandler}
                            {...onDeleteProps}
                            text="Delete"
                        />
                    }
                    <PrimaryButton
                        text={(onNextProps && onNextProps.text) || "Next"}
                        loading={loading}
                        onClick={onNextHandler}
                        {...onNextProps}
                    /></Stack>
            </Stack>
        </>
    );
};
export default CreateCourseWizard;
